import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Heart, Brain, Moon, Activity, Coffee, Users, 
  Wine, Cigarette, Phone, ShoppingBag, Utensils,
  Scale, Ruler, Target, AlertCircle, X, ArrowRight, Clock3, SmilePlus 
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import BMIGauge from "../components/BMIGauge";

const OnboardingPage = () => {
  const [formData, setFormData] = useState({
    // Original fields
    gender: "",
    age: "",
    weight: "",
    weightUnit: "kg",
    height: "",
    heightUnit: "cm",
    waist: "",
    dietaryHabits: "",
    sleepPatterns: "",
    healthGoals: "",
    healthConditions: [],
    symptoms: [],

    // New fields
    healthScale: 50,
    mood: "",
        // Updated mentalHealth object
        mentalHealth: {
          feelingDown: "",
          littleInterest: "",
          stressLevel: "",
          financialWorry: "",
          lifePurpose: "",
          reasonsForLiving: "",
          lifeValue: "",
          stressLevel: ""
        },
        relationships: {
          employmentStatus: "",
          hasListener: "",
          hasSupportiveNetwork: "",
          hasTrustedConfidant: "",
          companionshipFrequency: "",
          feelLeftOut: "",
          feelIsolated: "",
          feelLonely: ""
        },
       // Updated sleep object
       sleep: {
        nightShift: false,
        stayAwake: "",
        awakeTime: "",
        sleepDuration: "",
        timeAwakeAtNight: "",
        sleepSixToEight: "",
        nightShifts: "",
        satisfaction: "",
        stayAwake: "",
        sleepContinuity: "",
        caffeinatedDrinks: "",
        afternoonCaffeine: ""
      },
      physicalActivity: {
        workActivity: "",
        exercise: "",
        cycling: "",
        walking: "",
        houseworkChildcare: "",
        gardeningDIY: "",
        walkingPace: ""
      },
     // Updated diet object
     diet: {
      foodDiary: {
        breakfast: "",
        lunch: "",
        dinner: "",
        snacks: ""
      },
      typicalDay: true,
      fruitServings: "",
      vegetableServings: "",
      fermentedFoods: "",
      ultraProcessedFood: "",
      dietPattern: "",
      softDrinks: ""
    },
    // Updated substanceUse object
    substanceUse: {
      smoking: false,
      alcoholFrequency: "",
      alcoholAmount: "",
      bingeFrequency: "",
      recreationalDrugs: "",
      prescriptionMisuse: "",
      socialMediaTime: "",
      socialMediaAddiction: {
        cantStopThinking: false,
        wantMoreTime: false,
        failedToReduce: false,
        neglectActivities: false
      },
      otherAddictions: ""
    }
  });

  const [showSkipDialog, setShowSkipDialog] = useState(false);
  
  const handleSkip = () => {
    setShowSkipDialog(true);
  };

  const confirmSkip = async () => {
    if (user) {
      try {
        // Set minimal data in Firestore
        const onboardingDocRef = doc(db, "onboardingData", user.uid);
        await setDoc(onboardingDocRef, {
          setupCompleted: false,
          setupSkipped: true,
          skipDate: new Date().toISOString()
        }, { merge: true });
        
        // Show success message and redirect
        setSaveMessage("Setup skipped. You'll receive basic health recommendations. You can complete your profile anytime.");
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } catch (error) {
        console.error("Error saving skip status:", error);
        setSaveMessage("Failed to process your request. Please try again.");
      }
    }
  };

  const [bmi, setBmi] = useState(null);
  const [healthConditionInput, setHealthConditionInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
    const diseases = [
       "Diabetes", "Hypertension", "Asthma", "Arthritis", "Cancer", "Obesity", 
       "Allergies", "Cholesterol", "Heart Disease", "Migraine", "Alzheimer's", 
       "Parkinson's", "Osteoporosis", "Epilepsy", "Multiple Sclerosis", "Psoriasis", 
       "Influenza", "Bronchitis", "Pneumonia", "Tuberculosis", "Hepatitis", 
       "Lyme Disease", "Anemia", "Leukemia", "Lymphoma", "Crohn's Disease", 
       "Celiac Disease", "Eczema", "Gout", "Malaria", "Meningitis", "Endometriosis", 
       "Fibromyalgia", "Dengue", "HIV/AIDS", "Polio", "Thyroid Disorders", 
       "Vertigo", "Huntington's Disease", "Sickle Cell Disease", "Lupus", 
       "Chronic Fatigue Syndrome", "Cirrhosis", "Glaucoma", "Cataracts", 
       "Tinnitus", "Strep Throat", "Sinusitis", "Ulcerative Colitis", 
       "Kidney Stones", "Prostate Cancer", "Cervical Cancer", "Skin Cancer", 
       "Melanoma", "Depression", "Anxiety Disorders", "Bipolar Disorder", 
       "Schizophrenia", "ADHD", "Autism Spectrum Disorder", "PTSD", "Stroke", 
       "Arrhythmia", "Aneurysm", "Varicose Veins", "Hemophilia", 
       "Clotting Disorders", "Gangrene", "Herpes", "HPV", "Mononucleosis", 
       "Cholera", "Smallpox", "Measles", "Mumps", "Rubella", "Tetanus", 
       "Rabies", "Yellow Fever", "Zika Virus", "Ebola", "COVID-19", 
       "Cystic Fibrosis", "ALS", "Duchenne Muscular Dystrophy", "Myasthenia Gravis", 
       "Sarcoidosis", "Legionnaires' Disease", "Sepsis", "Pancreatitis", 
       "Pernicious Anemia", "Wilson's Disease", "Kawasaki Disease", 
       "Reye's Syndrome", "Hemochromatosis", "Irritable Bowel Syndrome (IBS)", 
       "GERD", "Peptic Ulcers", "Gallstones", "Pancreatic Cancer", "Liver Cancer", 
       "Esophageal Cancer", "Colon Cancer", "Rectal Cancer", "Bladder Cancer", 
       "Laryngeal Cancer", "Throat Cancer", "Tongue Cancer", "Leprosy", 
       "Scabies", "Ringworm", "Athlete's Foot", "Jock Itch", "Cellulitis", 
       "Impetigo", "Shingles", "Chickenpox", "Whooping Cough", "Anthrax", 
       "Botulism", "Toxoplasmosis", "Giardiasis", "Cryptosporidiosis", 
       "Hantavirus", "West Nile Virus", "Avian Flu", "Swine Flu", "MRSA", 
       "E. Coli Infection", "Salmonella Infection", "Campylobacter Infection", 
       "Clostridium Difficile", "Listeriosis", "Legionellosis", "Tularemia", 
       "Brucellosis", "Q Fever", "Typhoid Fever", "Paratyphoid Fever", 
       "Leptospirosis", "Yersiniosis", "Plague", "Trypanosomiasis", 
       "Chagas Disease", "Schistosomiasis", "Onchocerciasis", "Filariasis", 
       "Ascariasis", "Trichinosis", "Hookworm Infection", "Whipworm Infection", 
       "Pinworm Infection", "Strongyloidiasis", "Fascioliasis", "Paragonimiasis", 
       "Clonorchiasis", "Opisthorchiasis", "Taeniasis", "Cysticercosis", 
       "Echinococcosis", "Hydatid Disease", "Blastomycosis", "Histoplasmosis", 
       "Coccidioidomycosis", "Cryptococcosis", "Sporotrichosis", 
       "Aspergillosis", "Candida Infection", "Mucormycosis", "Toxocariasis", 
       "Bacterial Vaginosis", "Trichomoniasis", "Chlamydia", "Gonorrhea", 
       "Syphilis", "Pelvic Inflammatory Disease", "Genital Warts", 
       "Testicular Cancer", "Ovarian Cancer", "Breast Cancer", "Eye Cancer", 
       "Brain Cancer", "Spinal Cord Tumors", "Bone Cancer", "Soft Tissue Sarcomas", 
       "Neuroblastoma", "Retinoblastoma", "Wilms Tumor", "Langerhans Cell Histiocytosis", 
       "Hemangioma", "Capillary Malformation", "Lymphedema", "Edema", 
       "Tay-Sachs Disease", "Phenylketonuria", "Maple Syrup Urine Disease", 
       "Galactosemia", "Homocystinuria", "Alkaptonuria", "Porphyria", 
       "Urea Cycle Disorders", "Hyperammonemia", "Ornithine Transcarbamylase Deficiency", 
       "Mitochondrial Disorders", "Leigh Syndrome", "Kearns-Sayre Syndrome", 
       "Barth Syndrome", "Pearson Syndrome", "Wolfram Syndrome", 
       "DiGeorge Syndrome", "Noonan Syndrome", "Marfan Syndrome", 
       "Ehlers-Danlos Syndrome", "Loeys-Dietz Syndrome", "Turner Syndrome", 
       "Klinefelter Syndrome", "Fragile X Syndrome", "Prader-Willi Syndrome", 
       "Angelman Syndrome", "Williams Syndrome", "Smith-Magenis Syndrome", 
       "Rett Syndrome", "Cornelia de Lange Syndrome", "Cri du Chat Syndrome", 
       "Pitt-Hopkins Syndrome", "Kabuki Syndrome", "CHARGE Syndrome", 
       "VACTERL Association", "Tuberous Sclerosis", "Sturge-Weber Syndrome", 
       "Neurofibromatosis", "Von Hippel-Lindau Disease", "Li-Fraumeni Syndrome", 
       "Muir-Torre Syndrome", "Peutz-Jeghers Syndrome", "Gorlin Syndrome", 
       "Cowden Syndrome", "Birt-Hogg-Dube Syndrome", "Hereditary Angioedema", 
       "Xeroderma Pigmentosum", "Ataxia-Telangiectasia", "Bloom Syndrome", 
       "Cockayne Syndrome", "Fanconi Anemia", "Werner Syndrome", 
       "Progeria", "Batten Disease", "Canavan Disease", "Alexander Disease", 
       "Pelizaeus-Merzbacher Disease", "Krabbe Disease", "Fabry Disease", 
       "Gaucher Disease", "Niemann-Pick Disease", "Sandhoff Disease", 
       "Pompe Disease", "Mucopolysaccharidosis", "Hurler Syndrome", 
       "Hunter Syndrome", "Sanfilippo Syndrome", "Morquio Syndrome", 
       "Maroteaux-Lamy Syndrome", "Sly Syndrome", "Wolman Disease", 
       "Glycogen Storage Diseases", "Lafora Disease", "Unverricht-Lundborg Disease", 
       "Progressive Myoclonus Epilepsy", "Dravet Syndrome", "Lennox-Gastaut Syndrome", 
       "Infantile Spasms", "West Syndrome", "Joubert Syndrome", 
       "Meckel-Gruber Syndrome", "Senior-Loken Syndrome", "Bardet-Biedl Syndrome", 
       "Alstrom Syndrome", "Usher Syndrome", "Stickler Syndrome", 
       "Pierre Robin Sequence", "Treacher Collins Syndrome", "Goldenhar Syndrome", 
       "Crouzon Syndrome", "Apert Syndrome", "Saethre-Chotzen Syndrome", 
       "Pfeiffer Syndrome", "Muenke Syndrome", "Carpenter Syndrome", 
       "Bilateral Microtia", "Congenital Diaphragmatic Hernia", 
       "Omphalocele", "Gastroschisis", "Beckwith-Wiedemann Syndrome", 
       "Silver-Russell Syndrome", "Proteus Syndrome", "Bannayan-Riley-Ruvalcaba Syndrome", 
       "Simpson-Golabi-Behmel Syndrome", "Weaver Syndrome", "Sotos Syndrome", 
       "Costello Syndrome", "Cardiofaciocutaneous Syndrome", 
       "Cohen Syndrome", "Smith-Lemli-Opitz Syndrome", "Meckel Syndrome", 
       "Carney Complex", "MEN1 Syndrome", "MEN2 Syndrome", "Von Gierke Disease", 
       "McArdle Disease", "Tarui Disease", "Lactose Intolerance", 
       "Fructose Intolerance", "Congenital Adrenal Hyperplasia", 
       "Primary Ovarian Insufficiency", "Polycystic Ovary Syndrome", 
       "Amenorrhea", "Dysmenorrhea", "Menorrhagia", "Metrorrhagia", 
       "Endometrial Cancer", "Ovarian Cysts", "Uterine Fibroids", 
       "Pelvic Floor Disorders", "Interstitial Cystitis", 
       "Vulvodynia", "Dyspareunia", "Postpartum Depression", 
       "Perinatal Mood Disorders", "Gestational Diabetes", "Preeclampsia", 
       "HELLP Syndrome", "Placenta Previa", "Placental Abruption", 
       "Ectopic Pregnancy", "Molar Pregnancy", "Miscarriage", 
       "Stillbirth", "Neonatal Jaundice", "Kernicterus", 
       "Hypoxic-Ischemic Encephalopathy", "Birth Asphyxia", "Premature Birth", 
       "Low Birth Weight", "Neonatal Sepsis", "Neonatal Abstinence Syndrome", 
       "Neonatal Lupus", "Congenital Heart Defects", "Tetralogy of Fallot", 
       "Tricuspid Atresia", "Hypoplastic Left Heart Syndrome", 
       "Coarctation of the Aorta", "Transposition of the Great Arteries", 
       "Atrial Septal Defect", "Ventricular Septal Defect", 
       "Patent Ductus Arteriosus", "Congenital Diaphragmatic Hernia", 
       "Esophageal Atresia", "Tracheoesophageal Fistula", 
       "Cystic Hygroma", "Laryngeal Cleft", "Choanal Atresia", 
       "Congenital Hypothyroidism", "Congenital Hyperinsulinism", 
       "Beckwith-Wiedemann Syndrome", "Neonatal Seizures", "Inborn Errors of Metabolism",
       "Abdominal Pain", "Acid Reflux", "Heartburn", "Airsickness", 
       "Motion Sickness", "Bad Breath", "Belching", "Gas", "Bellyache", 
       "Abdominal Pain", "Bleeding", "Gastrointestinal Bleeding", 
       "Breath Odour", "Bad Breath", "Shortness of breath", "Breathlessness", 
       "Bruises", "Burping", "Gas", "Carsickness", "Motion Sickness", 
       "Chest Pain", "Chilblains", "Frostbite", "Choking", "Chronic Pain", 
       "Cluster Headache", "Headache", "Fever", "Hypothermia", "Constipation", 
       "Contusions", "Bruises", "Cough", "Dehydration", "Diarrhea", 
       "Dizziness", "Vertigo", "Dropsy", "Oedema", "Dysentery", "Diarrhea", 
       "Dysfunctional uterine bleeding", "Vaginal Bleeding", "Dyspepsia", 
       "Indigestion", "Dyspnea", "Breathing Problems", "Edema", "Fainting", 
       "Fatigue", "Fever", "Flatulence", "Frostbite", "Frostnip", 
       "Frostbite", "Gas", "Gastrointestinal Bleeding", 
       "GI Bleeding see Gastrointestinal Bleeding", "Halitosis", "Bad Breath", 
       "Headache", "Heartburn", "Heat Exhaustion", "Heat Illness", 
       "Heat Illness", "Choking", "Hematoma see Bleeding", 
       "Hemorrhage see Bleeding", "Hives", "Hot", "Hypothermia", "Icterus", 
       "Jaundice", "Indigestion", "Itching", "Jaundice", "Motion Sickness", 
       "Nausea", "Vomiting", "Abdominal see Abdominal Pain", "Chest Pain", 
       "Chronic Pain", "Pelvic Pain", "Pruritus", "Itching", "Pyrexia", 
       "Raynaud Phenomenon", "Raynaud's Disease", "Raynaud's Syndrome", 
       "Rectal Bleeding", "Brain fog", "Sciatica", "Seasickness", 
       "Motion Sickness", "Stammering", "Stuttering", "Stomach Ache", 
       "Abdominal Pain", "Stuttering", "Sunstroke", "Heat Illness", 
       "Swelling see Edema", "Syncope", "Fainting", "Tachypnea", 
       "Breathing Problems", "Tension Headache", "Headache", "Thirst", 
       "Dehydration", "Tiredness", "Fatigue", "Upset Stomach", "Urticaria", 
       "Hives", "Uterine bleeding", "Vaginal Bleeding", "Vaginal Bleeding", 
       "Vascular Headache", "Headache", "Vasovagal Syncope", "Fainting", 
       "Vertigo", "Dizziness", "Vertigo", "Vestibular Diseases", "Dizziness", 
       "Vertigo", "Vomiting", "Nausea", "Vomiting", "Weariness", "Fatigue"
     ];
     const [symptomInput, setSymptomInput] = useState("");
     const [symptomSuggestions, setSymptomSuggestions] = useState([]);
   
     const symptoms = [
       "Abdominal Pain",
       "Acid Reflux",
       "Airsickness",
       "Bad Breath",
       "Belching",
       "Bellyache",
       "Bleeding",
       "Bloating",
       "Blurred Vision",
       "Body Aches",
       "Brain Fog",
       "Breathlessness",
       "Bruising",
       "Chest Pain",
       "Chills",
       "Choking",
       "Cold Sweats",
       "Congestion",
       "Confusion",
       "Constipation",
       "Cough",
       "Cramps",
       "Dehydration",
       "Depression",
       "Diarrhea",
       "Difficulty Breathing",
       "Difficulty Swallowing",
       "Dizziness",
       "Double Vision",
       "Drowsiness",
       "Dry Eyes",
       "Dry Mouth",
       "Earache",
       "Eye Pain",
       "Eye Redness",
       "Fainting",
       "Fast Heart Rate",
       "Fatigue",
       "Fever",
       "Flatulence",
       "Frequent Urination",
       "Gastric Pain",
       "Gum Pain",
       "Hair Loss",
       "Hallucinations",
       "Headache",
       "Hearing Loss",
       "Heartburn",
       "Heat Exhaustion",
       "Heavy Menstrual Flow",
       "Hiccups",
       "High Blood Pressure",
       "Hives",
       "Hot Flashes",
       "Increased Appetite",
       "Indigestion",
       "Inflammation",
       "Insomnia",
       "Irregular Heartbeat",
       "Itching",
       "Joint Pain",
       "Light Sensitivity",
       "Loss of Appetite",
       "Loss of Balance",
       "Loss of Smell",
       "Loss of Taste",
       "Low Blood Pressure",
       "Memory Loss",
       "Menstrual Cramps",
       "Migraine",
       "Mood Swings",
       "Morning Sickness",
       "Muscle Pain",
       "Muscle Weakness",
       "Nausea",
       "Neck Pain",
       "Night Sweats",
       "Numbness",
       "Pain During Urination",
       "Palpitations",
       "Paralysis",
       "Phlegm",
       "Poor Concentration",
       "Rash",
       "Red Eyes",
       "Restlessness",
       "Ringing in Ears",
       "Runny Nose",
       "Seizures",
       "Shortness of Breath",
       "Shoulder Pain",
       "Skin Itching",
       "Sleep Problems",
       "Sleepiness",
       "Sleepwalking",
       "Slow Heart Rate",
       "Sneezing",
       "Sore Throat",
       "Stomach Pain",
       "Stuffy Nose",
       "Sweating",
       "Swelling",
       "Tingling",
       "Tooth Pain",
       "Tremors",
       "Urinary Problems",
       "Vertigo",
       "Vision Problems",
       "Vomiting",
       "Watery Eyes",
       "Weakness",
       "Weight Gain",
       "Weight Loss",
       "Wheezing"
   ];
  
  


  const handleSymptomChange = (e) => {
    const value = e.target.value;
    setSymptomInput(value);
  
    if (value) {
      const filteredSuggestions = symptoms.filter((symptom) =>
        symptom.toLowerCase().startsWith(value.toLowerCase())
      );
      setSymptomSuggestions(filteredSuggestions);
    } else {
      setSymptomSuggestions([]);
    }
  };
  
  const addSymptom = (symptom) => {
    if (!formData.symptoms.includes(symptom)) {
      setFormData({
        ...formData,
        symptoms: [...formData.symptoms, symptom],
      });
    }
    setSymptomInput("");
    setSymptomSuggestions([]);
  };
  
  const removeSymptom = (symptom) => {
    setFormData({
      ...formData,
      symptoms: formData.symptoms.filter(
        (currentSymptom) => currentSymptom !== symptom
      ),
    });
  };

    // Handle nested object changes
    const handleNestedChange = (category, field, value) => {
      setFormData(prev => ({
        ...prev,
        [category]: {
          ...prev[category],
          [field]: value
        }
      }));
    };

      // Handle checkbox changes for social media addiction
  const handleSocialMediaAddiction = (field) => {
    setFormData(prev => ({
      ...prev,
      substanceUse: {
        ...prev.substanceUse,
        socialMediaAddiction: {
          ...prev.substanceUse.socialMediaAddiction,
          [field]: !prev.substanceUse.socialMediaAddiction[field]
        }
      }
    }));
  };

  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          // Fetch the user's onboarding data
          const onboardingDocRef = doc(db, "onboardingData", currentUser.uid);
          const onboardingDocSnap = await getDoc(onboardingDocRef);

          // Fetch the user's health data for the last weight and sleep hours
          const healthDataDocRef = doc(db, "healthData", currentUser.uid);
          const healthDataDocSnap = await getDoc(healthDataDocRef);

          let lastWeight = "";
          let lastSleepHours = "";

          if (healthDataDocSnap.exists()) {
            const healthData = healthDataDocSnap.data();

            // Get the last weight and sleep hours entries
            if (healthData.weightTrends && healthData.weightTrends.length > 0) {
              lastWeight = healthData.weightTrends[healthData.weightTrends.length - 1].weight;
            }

            if (healthData.sleepPatterns && healthData.sleepPatterns.length > 0) {
              lastSleepHours = healthData.sleepPatterns[healthData.sleepPatterns.length - 1].hours;
            }
          }

          setFormData({
            ...formData,
            ...onboardingDocSnap.data(),
            weight: lastWeight || onboardingDocSnap.data()?.weight || "",
            sleepPatterns: lastSleepHours || onboardingDocSnap.data()?.sleepPatterns || "",
          });
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      }

      setLoading(false); // Once data is fetched, set loading to false
    });

    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleWeightUnitChange = () => {
    const newUnit = formData.weightUnit === "kg" ? "lbs" : "kg";
    const convertedWeight = formData.weight
      ? formData.weightUnit === "kg"
        ? (formData.weight * 2.20462).toFixed(2)
        : (formData.weight / 2.20462).toFixed(2)
      : "";

    setFormData({ ...formData, weight: convertedWeight, weightUnit: newUnit });
  };

  const handleHeightUnitChange = () => {
    const newUnit = formData.heightUnit === "cm" ? "feet and inches" : "cm";
    const convertedHeight = formData.height
      ? formData.heightUnit === "cm"
        ? (formData.height / 30.48).toFixed(2)
        : (formData.height * 30.48).toFixed(2)
      : "";

    setFormData({ ...formData, height: convertedHeight, heightUnit: newUnit });
  };
  
  const handleHealthConditionChange = (e) => {
    const value = e.target.value;
    setHealthConditionInput(value);

    if (value) {
      const filteredSuggestions = diseases.filter((disease) =>
        disease.toLowerCase().startsWith(value.toLowerCase())
      );
      setSuggestions(filteredSuggestions);
    } else {
      setSuggestions([]);
    }
  };

  const addHealthCondition = (condition) => {
    if (!formData.healthConditions.includes(condition)) {
      setFormData({
        ...formData,
        healthConditions: [...formData.healthConditions, condition],
      });
    }
    setHealthConditionInput("");
    setSuggestions([]);
  };

  const removeHealthCondition = (condition) => {
    setFormData({
      ...formData,
      healthConditions: formData.healthConditions.filter(
        (healthCondition) => healthCondition !== condition
      ),
    });
  };

  const calculateBMI = () => {
    if (formData.weight && formData.height) {
      const weightInKg = formData.weightUnit === "kg" ? formData.weight : formData.weight / 2.20462;
      const heightInMeters = formData.heightUnit === "cm" ? formData.height / 100 : formData.height * 0.3048;
      const calculatedBmi = (weightInKg / (heightInMeters * heightInMeters)).toFixed(2);
      setBmi(calculatedBmi);
    } else {
      setBmi(null);
    }
  };

  useEffect(() => {
    calculateBMI();
  }, [formData.weight, formData.height, formData.weightUnit, formData.heightUnit]);

  const handleSave = async () => {
    if (user) {
      try {
        const onboardingDocRef = doc(db, "onboardingData", user.uid);
        const healthDataDocRef = doc(db, "healthData", user.uid);
        const currentDate = new Date().toISOString();
  
        const healthDataSnap = await getDoc(healthDataDocRef);
        const existingHealthData = healthDataSnap.exists() ? healthDataSnap.data() : {};
  
        // Ensure weightTrends array exists
        let updatedWeightTrends = existingHealthData.weightTrends ? [...existingHealthData.weightTrends] : [];
  
        // Update the last recorded weight instead of pushing a new entry
        if (updatedWeightTrends.length > 0) {
          updatedWeightTrends[updatedWeightTrends.length - 1].weight = formData.weight;
        } else {
          updatedWeightTrends.push({ date: currentDate, weight: formData.weight });
        }
        const updatedMoodTrends = [
          ...(existingHealthData.moodTrends || []),
          {
            date: currentDate,
            mood: formData.mood
          }
        ];
  
  
        const healthTrendsUpdate = {
          ...existingHealthData, // Preserve other existing data
          moodTrends: updatedMoodTrends,
          weightTrends: updatedWeightTrends, // Updated weight trends
          moodTrends: [...(existingHealthData.moodTrends || []), {
            date: currentDate,
            mood: formData.mood,
            healthScale: formData.healthScale
          }],
          sleepPatterns: [...(existingHealthData.sleepPatterns || []), {
            date: currentDate,
            satisfaction: formData.sleep.satisfaction,
            duration: formData.sleep.sleepDuration,
            caffeinatedDrinks: formData.sleep.caffeinatedDrinks
          }],
          mentalHealthTrends: [...(existingHealthData.mentalHealthTrends || []), {
            date: currentDate,
            feelingDown: formData.mentalHealth.feelingDown,
            stressLevel: formData.mentalHealth.stressLevel,
            lifePurpose: formData.mentalHealth.lifePurpose
          }],
          physicalActivityTrends: [...(existingHealthData.physicalActivityTrends || []), {
            date: currentDate,
            workActivity: formData.physicalActivity.workActivity,
            exercise: formData.physicalActivity.exercise,
            walking: formData.physicalActivity.walking
          }],
          dietaryTrends: [...(existingHealthData.dietaryTrends || []), {
            date: currentDate,
            fruitServings: formData.diet.fruitServings,
            vegetableServings: formData.diet.vegetableServings,
            ultraProcessedFood: formData.diet.ultraProcessedFood,
            dietPattern: formData.diet.dietPattern
          }],
          substanceUseTrends: [...(existingHealthData.substanceUseTrends || []), {
            date: currentDate,
            alcoholFrequency: formData.substanceUse.alcoholFrequency,
            smokingStatus: formData.substanceUse.smoking,
            socialMediaTime: formData.substanceUse.socialMediaTime
          }],
          relationshipTrends: [...(existingHealthData.relationshipTrends || []), {
            date: currentDate,
            employmentStatus: formData.relationships.employmentStatus,
            socialSupport: formData.relationships.hasListener,
            lonelinessLevel: formData.relationships.feelLonely
          }],
          lastUpdated: currentDate
        };
  
        await Promise.all([
          setDoc(onboardingDocRef, { ...formData, lastMoodUpdate: currentDate }, { merge: true }),
          setDoc(healthDataDocRef, healthTrendsUpdate, { merge: true })
        ]);
  
        setIsSaved(true);
        setSaveMessage("Health profile saved successfully! Redirecting to home...");
        setTimeout(() => {
          navigate("/");
        }, 2000);
      } catch (error) {
        console.error("Error saving data to Firebase:", error);
        setSaveMessage("Failed to save health profile. Please try again.");
      }
    } else {
      setSaveMessage("You must be logged in to save your data.");
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSave();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-gradient-to-br from-teal-500 to-blue-700 text-white">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-teal-500 to-blue-700 text-white">
      <Header />

      <main className="flex-grow flex flex-col items-center justify-center relative p-6">
        <form
          onSubmit={handleSubmit}
          className="bg-white text-gray-800 rounded-lg shadow-lg p-8 max-w-2xl w-full"
        >
          <h2 className="text-2xl font-bold text-center mb-6 text-blue-600">
            Personalize Your Health Profile
          </h2>

          <div className="mb-4">
            <label className="font-semibold">Weight ({formData.weightUnit})</label>
            <input
              type="number"
              name="weight"
              value={formData.weight}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg"
            />
            <button
              type="button"
              onClick={handleWeightUnitChange}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg"
            >
              Switch to {formData.weightUnit === "kg" ? "lbs" : "kg"}
            </button>
          </div>

          <div className="mb-4">
            <label className="font-semibold">Height ({formData.heightUnit})</label>
            <input
              type="number"
              name="height"
              value={formData.height}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg"
            />
            <button
              type="button"
              onClick={handleHeightUnitChange}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-lg"
            >
              Switch to {formData.heightUnit === "cm" ? "feet and inches" : "cm"}
            </button>
          </div>

          <div className="mb-4">
  <label className="font-semibold">BMI</label>
  {bmi ? (
    <BMIGauge bmi={parseFloat(bmi)} />
  ) : (
    <p className="w-full px-4 py-2 border rounded-lg bg-gray-100">
      Enter weight and height to calculate BMI
    </p>
  )}
</div>

<div className="mb-8">
  <h3 className="text-xl font-semibold mb-4 flex items-center">
    <SmilePlus className="mr-2 text-yellow-500" />
    How are you feeling today?
  </h3>
  <div className="grid grid-cols-5 gap-4">
    {[
      { emoji: "😊", value: "Happy", label: "Happy" },
      { emoji: "😐", value: "Indifferent", label: "Indifferent" },
      { emoji: "😢", value: "Sad", label: "Sad" },
      { emoji: "😠", value: "Angry", label: "Angry" },
      { emoji: "😟", value: "Worried", label: "Worried" }
    ].map(({ emoji, value, label }) => (
      <button
        key={value}
        type="button"
        onClick={() => setFormData({ ...formData, mood: value })}
        className={`p-4 text-center rounded-lg transition-all duration-200 hover:bg-gray-100 ${
          formData.mood === value ? 'bg-blue-100 border-2 border-blue-500' : 'border-2 border-transparent'
        }`}
      >
        <div className="text-3xl mb-2">{emoji}</div>
        <div className="text-sm font-medium">{label}</div>
      </button>
    ))}
  </div>
</div>

          <div className="mb-4">
            <label className="font-semibold">Waist (cm)</label>
            <input
              type="number"
              name="waist"
              value={formData.waist}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg"
            />
          </div>

          <div className="mb-4">
            <label className="font-semibold">Dietary Habits</label>
            <select
              name="dietaryHabits"
              value={formData.dietaryHabits}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">Select</option>
              <option value="Vegetarian">Vegetarian</option>
              <option value="Non-Vegetarian">Non-Vegetarian</option>
              <option value="Vegan">Vegan</option>
              <option value="Keto">Keto</option>
              <option value="Pescatarian">Pescatarian</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="font-semibold">Health Goals</label>
            <select
              name="healthGoals"
              value={formData.healthGoals}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">Select</option>
              <option value="Weight Loss">Weight Loss</option>
              <option value="Muscle Gain">Muscle Gain</option>
              <option value="Improve Stamina">Improve Stamina</option>
              <option value="Better Sleep">Better Sleep</option>
              <option value="Overall Fitness">Overall Fitness</option>
            </select>
          </div>

          <div className="mb-4">
            <label className="font-semibold">Health Conditions</label>
            <input
              type="text"
              value={healthConditionInput}
              onChange={handleHealthConditionChange}
              placeholder="Type to search..."
              className="w-full px-4 py-2 border rounded-lg"
            />
            <div className="mt-2">
              {suggestions.map((condition) => (
                <button
                  key={condition}
                  type="button"
                  onClick={() => addHealthCondition(condition)}
                  className="mr-2 mb-2 px-4 py-1 bg-gray-200 rounded-lg"
                >
                  {condition}
                </button>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap">
                  {formData.healthConditions.map((condition, index) => (
                    <span
                      key={index}
                      className="mr-2 mb-2 px-4 py-1 bg-green-500 text-white rounded-lg flex items-center"
                    >
                      {condition}
                      <button
                        type="button"
                        onClick={() => removeHealthCondition(condition)}
                        className="ml-2 bg-transparent text-white"
                      >
                        <img
                          src="https://img.icons8.com/ios-filled/50/ffffff/delete-forever.png" // Example cross icon URL
                          alt="remove"
                          className="w-4 h-4 transition-all duration-200 ease-in-out hover:filter hover:brightness-0 hover:saturate-200 hover:hue-rotate-90"
                        />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="mb-4">
  <label className="font-semibold">Symptoms</label>
  <input
    type="text"
    value={symptomInput}
    onChange={handleSymptomChange}
    placeholder="Type to search symptoms..."
    className="w-full px-4 py-2 border rounded-lg"
  />
  <div className="mt-2">
    {symptomSuggestions.map((symptom) => (
      <button
        key={symptom}
        type="button"
        onClick={() => addSymptom(symptom)}
        className="mr-2 mb-2 px-4 py-1 bg-gray-200 rounded-lg"
      >
        {symptom}
      </button>
    ))}
  </div>
  <div className="mt-2 flex flex-wrap">
    {formData.symptoms.map((symptom, index) => (
      <span
        key={index}
        className="mr-2 mb-2 px-4 py-1 bg-green-500 text-white rounded-lg flex items-center"
      >
        {symptom}
        <button
          type="button"
          onClick={() => removeSymptom(symptom)}
          className="ml-2 bg-transparent text-white"
        >
          <img
            src="https://img.icons8.com/ios-filled/50/ffffff/delete-forever.png"
            alt="remove"
            className="w-4 h-4 transition-all duration-200 ease-in-out hover:filter hover:brightness-0 hover:saturate-200 hover:hue-rotate-90"
          />
        </button>
      </span>
    ))}
  </div>
</div>

                    {/* Health Scale 
                    <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Heart className="mr-2 text-red-500" />
              Overall Health Assessment
            </h3>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={formData.healthScale}
              onChange={(e) => setFormData({...formData, healthScale: e.target.value})}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between text-sm mt-2">
              <span>Worst health (0)</span>
              <span>Best health (100)</span>
            </div>
          </div>*/}


         {/* Updated Mental Health Section */}
<div className="mb-8">
  <h3 className="text-xl font-semibold mb-4 flex items-center">
    <Brain className="mr-2 text-purple-500" />
    Mental Wellbeing
  </h3>
  <div className="grid grid-cols-1 gap-4">
    {/* Little Interest */}
    <label htmlFor="littleInterest" className="font-medium">
      Little interest or pleasure in doing things?
    </label>
    <select
      id="littleInterest"
      name="littleInterest"
      value={formData.mentalHealth.littleInterest}
      onChange={(e) => handleNestedChange('mentalHealth', 'littleInterest', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="not_at_all">Not at all</option>
      <option value="several_days">Several days</option>
      <option value="more_than_half">More than half the days</option>
      <option value="nearly_every_day">Nearly every day</option>
    </select>

    {/* Financial Worry */}
    <label htmlFor="financialWorry" className="font-medium">
      Worry about making ends meet financially?
    </label>
    <select
      id="financialWorry"
      name="financialWorry"
      value={formData.mentalHealth.financialWorry}
      onChange={(e) => handleNestedChange('mentalHealth', 'financialWorry', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="never">Never</option>
      <option value="sometimes">Sometimes</option>
      <option value="often">Often</option>
      <option value="always">Always</option>
    </select>

    {/* Life Purpose */}
    <label htmlFor="lifePurpose" className="font-medium">
      My life has a clear sense of purpose
    </label>
    <select
      id="lifePurpose"
      name="lifePurpose"
      value={formData.mentalHealth.lifePurpose}
      onChange={(e) => handleNestedChange('mentalHealth', 'lifePurpose', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="strongly_disagree">Strongly disagree</option>
      <option value="disagree">Disagree</option>
      <option value="neutral">Neither agree nor disagree</option>
      <option value="agree">Agree</option>
      <option value="strongly_agree">Strongly agree</option>
    </select>

    {/* Reasons for Living */}
    <label htmlFor="reasonsForLiving" className="font-medium">
      I have lots of reasons for living
    </label>
    <select
      id="reasonsForLiving"
      name="reasonsForLiving"
      value={formData.mentalHealth.reasonsForLiving}
      onChange={(e) => handleNestedChange('mentalHealth', 'reasonsForLiving', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="strongly_disagree">Strongly disagree</option>
      <option value="disagree">Disagree</option>
      <option value="neutral">Neither agree nor disagree</option>
      <option value="agree">Agree</option>
      <option value="strongly_agree">Strongly agree</option>
    </select>

    {/* Life Value */}
    <label htmlFor="lifeValue" className="font-medium">
      What I do in my life is valuable and worthwhile
    </label>
    <select
      id="lifeValue"
      name="lifeValue"
      value={formData.mentalHealth.lifeValue}
      onChange={(e) => handleNestedChange('mentalHealth', 'lifeValue', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="strongly_disagree">Strongly disagree</option>
      <option value="disagree">Disagree</option>
      <option value="neutral">Neither agree nor disagree</option>
      <option value="agree">Agree</option>
      <option value="strongly_agree">Strongly agree</option>
    </select>
  </div>
</div>


 {/* Updated Sleep Section */}
 <div className="mb-8">
      <h3 className="text-xl font-semibold mb-4 flex items-center">
        <Moon className="mr-2 text-blue-500" />
        Sleep Patterns
      </h3>
      
      <div className="grid grid-cols-1 gap-4">
        {/* Night Shifts */}
        <label htmlFor="nightShifts" className="font-medium">
          Do you work night shifts?
        </label>
        <select
          id="nightShifts"
          name="nightShifts"
          value={formData.sleep.nightShifts}
          onChange={(e) => handleNestedChange('sleep', 'nightShifts', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="">Select an option</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>

        {/* Sleep Satisfaction */}
        <label htmlFor="satisfaction" className="font-medium">
          How satisfied are you with your sleep?
        </label>
        <select
          id="satisfaction"
          name="satisfaction"
          value={formData.sleep.satisfaction}
          onChange={(e) => handleNestedChange('sleep', 'satisfaction', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="">Select an option</option>
          <option value="Rarely">Rarely</option>
          <option value="Sometimes">Sometimes</option>
          <option value="Usually/Always">Usually/Always</option>
        </select>

        {/* Stay Awake All Day */}
        <label htmlFor="stayAwake" className="font-medium">
          Stay awake all day without dozing?
        </label>
        <select
          id="stayAwake"
          name="stayAwake"
          value={formData.sleep.stayAwake}
          onChange={(e) => handleNestedChange('sleep', 'stayAwake', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="">Select an option</option>
          <option value="Rarely">Rarely</option>
          <option value="Sometimes">Sometimes</option>
          <option value="Usually/Always">Usually/Always</option>
        </select>

        {/* Sleep Continuity */}
        <label htmlFor="sleepContinuity" className="font-medium">
          Do you sleep continuously through the night?
        </label>
        <select
          id="sleepContinuity"
          name="sleepContinuity"
          value={formData.sleep.sleepContinuity}
          onChange={(e) => handleNestedChange('sleep', 'sleepContinuity', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="">Select an option</option>
          <option value="Rarely">Rarely</option>
          <option value="Sometimes">Sometimes</option>
          <option value="Usually/Always">Usually/Always</option>
        </select>

        {/* Sleep Duration */}
        <label htmlFor="sleepDuration" className="font-medium">
          Do you get 6-8 hours of sleep per night?
        </label>
        <select
          id="sleepDuration"
          name="sleepDuration"
          value={formData.sleep.sleepDuration}
          onChange={(e) => handleNestedChange('sleep', 'sleepDuration', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="">Select an option</option>
          <option value="Rarely">Rarely</option>
          <option value="Sometimes">Sometimes</option>
          <option value="Usually/Always">Usually/Always</option>
        </select>

        {/* Caffeinated Drinks Count */}
        <label htmlFor="caffeinatedDrinks" className="font-medium">
          How many cups of caffeinated drinks do you consume daily?
          (including coffee, tea, soda, and energy drinks)
        </label>
        <select
          id="caffeinatedDrinks"
          name="caffeinatedDrinks"
          value={formData.sleep.caffeinatedDrinks}
          onChange={(e) => handleNestedChange('sleep', 'caffeinatedDrinks', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="">Select an option</option>
          <option value="0">0</option>
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
          <option value="More than 3">More than 3</option>
        </select>

        {/* Afternoon Caffeine */}
        <label htmlFor="afternoonCaffeine" className="font-medium">
          Do you consume caffeinated drinks after mid-day?
        </label>
        <select
          id="afternoonCaffeine"
          name="afternoonCaffeine"
          value={formData.sleep.afternoonCaffeine}
          onChange={(e) => handleNestedChange('sleep', 'afternoonCaffeine', e.target.value)}
          className="w-full px-4 py-2 border rounded-lg"
        >
          <option value="">Select an option</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>
    </div>
  

          
{/* Updated Diet Section */}
<div className="mb-8">
  <h3 className="text-xl font-semibold mb-4 flex items-center">
    <Utensils className="mr-2 text-yellow-500" />
    Eating Patterns
  </h3>
  <div className="grid grid-cols-1 gap-4">
    {/* Daily Food Diary
    <div className="space-y-4">
      <h4 className="font-medium">Daily Food Diary</h4>

      <label htmlFor="breakfast" className="font-medium">Breakfast</label>
      <textarea
        id="breakfast"
        name="breakfast"
        placeholder="Breakfast"
        value={formData.diet.foodDiary.breakfast}
        onChange={(e) => handleNestedChange('diet', 'foodDiary', {
          ...formData.diet.foodDiary,
          breakfast: e.target.value
        })}
        className="w-full px-4 py-2 border rounded-lg"
        rows="2"
      />

      <label htmlFor="lunch" className="font-medium">Lunch</label>
      <textarea
        id="lunch"
        name="lunch"
        placeholder="Lunch"
        value={formData.diet.foodDiary.lunch}
        onChange={(e) => handleNestedChange('diet', 'foodDiary', {
          ...formData.diet.foodDiary,
          lunch: e.target.value
        })}
        className="w-full px-4 py-2 border rounded-lg"
        rows="2"
      />

      <label htmlFor="dinner" className="font-medium">Dinner</label>
      <textarea
        id="dinner"
        name="dinner"
        placeholder="Dinner"
        value={formData.diet.foodDiary.dinner}
        onChange={(e) => handleNestedChange('diet', 'foodDiary', {
          ...formData.diet.foodDiary,
          dinner: e.target.value
        })}
        className="w-full px-4 py-2 border rounded-lg"
        rows="2"
      />

      <label htmlFor="snacks" className="font-medium">Snacks</label>
      <textarea
        id="snacks"
        name="snacks"
        placeholder="Snacks"
        value={formData.diet.foodDiary.snacks}
        onChange={(e) => handleNestedChange('diet', 'foodDiary', {
          ...formData.diet.foodDiary,
          snacks: e.target.value
        })}
        className="w-full px-4 py-2 border rounded-lg"
        rows="2"
      />
    </div> */}

    {/* Typical Day Checkbox 
    <div className="flex items-center">
      <input
        id="typicalDay"
        name="typicalDay"
        type="checkbox"
        checked={formData.diet.typicalDay}
        onChange={(e) => handleNestedChange('diet', 'typicalDay', e.target.checked)}
        className="mr-2"
      />
      <label htmlFor="typicalDay">Was this a typical day for you?</label>
    </div>*/}

    {/* Fermented Foods Consumption */}
    <label htmlFor="fermentedFoods" className="font-medium">Fermented foods consumption</label>
    <select
      id="fermentedFoods"
      name="fermentedFoods"
      value={formData.diet.fermentedFoods}
      onChange={(e) => handleNestedChange('diet', 'fermentedFoods', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="daily">Daily</option>
      <option value="weekly">Weekly</option>
      <option value="monthly">Monthly</option>
      <option value="rarely">Rarely</option>
      <option value="never">Never</option>
    </select>
  </div>
</div>


 {/* Updated Substance Use Section */}
<div className="mb-8">
  <h3 className="text-xl font-semibold mb-4 flex items-center">
    <AlertCircle className="mr-2 text-red-500" />
    Substance Use & Behaviors
  </h3>
  <div className="grid grid-cols-1 gap-4">
    {/* Alcohol Amount */}
    <label htmlFor="alcoholAmount" className="font-medium">Drinks on a typical day when drinking</label>
    <select
      id="alcoholAmount"
      name="alcoholAmount"
      value={formData.substanceUse.alcoholAmount}
      onChange={(e) => handleNestedChange('substanceUse', 'alcoholAmount', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="1_2">1-2 drinks</option>
      <option value="3_4">3-4 drinks</option>
      <option value="5_6">5-6 drinks</option>
      <option value="7_9">7-9 drinks</option>
      <option value="10_plus">10+ drinks</option>
    </select>

    {/* Recreational Drugs Use */}
    <label htmlFor="recreationalDrugs" className="font-medium">Recreational drugs use in last 12 months</label>
    <select
      id="recreationalDrugs"
      name="recreationalDrugs"
      value={formData.substanceUse.recreationalDrugs}
      onChange={(e) => handleNestedChange('substanceUse', 'recreationalDrugs', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="never">Never</option>
      <option value="less_than_monthly">Less than monthly</option>
      <option value="monthly">Monthly</option>
      <option value="weekly">Weekly</option>
      <option value="daily">Daily or almost daily</option>
    </select>

    {/* Prescription Medication Misuse */}
    <label htmlFor="prescriptionMisuse" className="font-medium">Prescription medication misuse in last 12 months</label>
    <select
      id="prescriptionMisuse"
      name="prescriptionMisuse"
      value={formData.substanceUse.prescriptionMisuse}
      onChange={(e) => handleNestedChange('substanceUse', 'prescriptionMisuse', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="never">Never</option>
      <option value="less_than_monthly">Less than monthly</option>
      <option value="monthly">Monthly</option>
      <option value="weekly">Weekly</option>
      <option value="daily">Daily or almost daily</option>
    </select>

    {/* Other Addictions */}
    <label htmlFor="otherAddictions" className="font-medium">Other addictions or behaviors you struggle to control (optional)</label>
    <textarea
      id="otherAddictions"
      name="otherAddictions"
      placeholder="Other addictions or behaviors you struggle to control (optional)"
      value={formData.substanceUse.otherAddictions}
      onChange={(e) => handleNestedChange('substanceUse', 'otherAddictions', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
      rows="3"
    />
  </div>
</div>
<div className="mb-8">
      <h3 className="text-xl font-semibold mb-4 flex items-center">
        <Activity className="mr-2 text-blue-500" />
        Physical Activity
      </h3>

      <div className="grid grid-cols-1 gap-4">
        {/* Work Activity */}
        <div>
          <label htmlFor="workActivity" className="font-medium">
            Level of physical activity at work
          </label>
          <select
            id="workActivity"
            name="workActivity"
            value={formData.physicalActivity.workActivity}
            onChange={(e) => handleNestedChange('physicalActivity', 'workActivity', e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mt-1"
          >
            <option value="">Select an option</option>
            <option value="Not employed">Not employed</option>
            <option value="Mostly sitting">Mostly sitting</option>
            <option value="Standing/walking">Standing/walking</option>
            <option value="Moderate physical activity">Moderate physical activity</option>
            <option value="Vigorous physical activity">Vigorous physical activity</option>
          </select>
        </div>

        <h4 className="font-medium mt-2">Weekly Exercise Hours</h4>

        {/* Exercise */}
        <div>
          <label htmlFor="exercise" className="font-medium">
            Exercise (gym, sports, fitness classes)
          </label>
          <select
            id="exercise"
            name="exercise"
            value={formData.physicalActivity.exercise}
            onChange={(e) => handleNestedChange('physicalActivity', 'exercise', e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mt-1"
          >
            <option value="">Select an option</option>
            <option value="None">None</option>
            <option value="<1 hour">Less than 1 hour</option>
            <option value="1-3 hours">1-3 hours</option>
            <option value=">3 hours">More than 3 hours</option>
          </select>
        </div>

        {/* Cycling */}
        <div>
          <label htmlFor="cycling" className="font-medium">
            Cycling
          </label>
          <select
            id="cycling"
            name="cycling"
            value={formData.physicalActivity.cycling}
            onChange={(e) => handleNestedChange('physicalActivity', 'cycling', e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mt-1"
          >
            <option value="">Select an option</option>
            <option value="None">None</option>
            <option value="<1 hour">Less than 1 hour</option>
            <option value="1-3 hours">1-3 hours</option>
            <option value=">3 hours">More than 3 hours</option>
          </select>
        </div>

        {/* Walking */}
        <div>
          <label htmlFor="walking" className="font-medium">
            Walking
          </label>
          <select
            id="walking"
            name="walking"
            value={formData.physicalActivity.walking}
            onChange={(e) => handleNestedChange('physicalActivity', 'walking', e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mt-1"
          >
            <option value="">Select an option</option>
            <option value="None">None</option>
            <option value="<1 hour">Less than 1 hour</option>
            <option value="1-3 hours">1-3 hours</option>
            <option value=">3 hours">More than 3 hours</option>
          </select>
        </div>

        {/* Housework */}
        <div>
          <label htmlFor="housework" className="font-medium">
            Housework/Childcare
          </label>
          <select
            id="housework"
            name="housework"
            value={formData.physicalActivity.housework}
            onChange={(e) => handleNestedChange('physicalActivity', 'housework', e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mt-1"
          >
            <option value="">Select an option</option>
            <option value="None">None</option>
            <option value="<1 hour">Less than 1 hour</option>
            <option value="1-3 hours">1-3 hours</option>
            <option value=">3 hours">More than 3 hours</option>
          </select>
        </div>

        {/* Gardening */}
        <div>
          <label htmlFor="gardening" className="font-medium">
            Gardening/DIY
          </label>
          <select
            id="gardening"
            name="gardening"
            value={formData.physicalActivity.gardening}
            onChange={(e) => handleNestedChange('physicalActivity', 'gardening', e.target.value)}
            className="w-full px-4 py-2 border rounded-lg mt-1"
          >
            <option value="">Select an option</option>
            <option value="None">None</option>
            <option value="<1 hour">Less than 1 hour</option>
            <option value="1-3 hours">1-3 hours</option>
            <option value=">3 hours">More than 3 hours</option>
          </select>
        </div>
      </div>
    </div>


 {/* Relationships Section */}
<div className="mb-8">
  <h3 className="text-xl font-semibold mb-4 flex items-center">
    <Users className="mr-2 text-indigo-500" />
    Relationships & Support Network
  </h3>
  <div className="grid grid-cols-1 gap-4">
    {/* Supportive Network */}
    <label htmlFor="hasSupportiveNetwork" className="font-medium">I feel there are people I can talk to if I am upset</label>
    <select
      id="hasSupportiveNetwork"
      name="hasSupportiveNetwork"
      value={formData.relationships.hasSupportiveNetwork}
      onChange={(e) => handleNestedChange('relationships', 'hasSupportiveNetwork', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="never">Never</option>
      <option value="rarely">Rarely</option>
      <option value="sometimes">Sometimes</option>
      <option value="usually">Usually</option>
      <option value="always">Always</option>
    </select>

    {/* Trusted Confidant */}
    <label htmlFor="hasTrustedConfidant" className="font-medium">I have someone I trust to talk with about my feelings</label>
    <select
      id="hasTrustedConfidant"
      name="hasTrustedConfidant"
      value={formData.relationships.hasTrustedConfidant}
      onChange={(e) => handleNestedChange('relationships', 'hasTrustedConfidant', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="never">Never</option>
      <option value="rarely">Rarely</option>
      <option value="sometimes">Sometimes</option>
      <option value="usually">Usually</option>
      <option value="always">Always</option>
    </select>

    {/* Companionship Frequency */}
    <label htmlFor="companionshipFrequency" className="font-medium">How often do you feel you lack companionship?</label>
    <select
      id="companionshipFrequency"
      name="companionshipFrequency"
      value={formData.relationships.companionshipFrequency}
      onChange={(e) => handleNestedChange('relationships', 'companionshipFrequency', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="hardly_ever">Hardly ever or never</option>
      <option value="sometimes">Some of the time</option>
      <option value="often">Often</option>
    </select>

    {/* Feel Isolated */}
    <label htmlFor="feelIsolated" className="font-medium">How often do you feel isolated from others?</label>
    <select
      id="feelIsolated"
      name="feelIsolated"
      value={formData.relationships.feelIsolated}
      onChange={(e) => handleNestedChange('relationships', 'feelIsolated', e.target.value)}
      className="w-full px-4 py-2 border rounded-lg"
    >
      <option value="">Select an option</option>
      <option value="hardly_ever">Hardly ever or never</option>
      <option value="sometimes">Some of the time</option>
      <option value="often">Often</option>
    </select>
  </div>
</div>


{/* Social Media Addiction Section */}
<div className="mb-8">
  <h3 className="text-xl font-semibold mb-4 flex items-center">
    <Phone className="mr-2 text-blue-500" />
    Social Media Usage
  </h3>

  {/* Social Media Usage Time */}
  <label htmlFor="socialMediaTime" className="font-medium">Daily social media usage</label>
  <select
    id="socialMediaTime"
    name="socialMediaTime"
    value={formData.substanceUse.socialMediaTime}
    onChange={(e) => handleNestedChange('substanceUse', 'socialMediaTime', e.target.value)}
    className="w-full px-4 py-2 border rounded-lg mb-4"
  >
    <option value="">Select an option</option>
    <option value="none">Not at all</option>
    <option value="1hr_week">1 hour a week</option>
    <option value="1hr_day">1 hour daily</option>
    <option value="4hr_day">4 hours daily</option>
    <option value="most_day">Most of the day</option>
  </select>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    {/* Can't Stop Thinking */}
    <div className="flex items-center">
      <input
        type="checkbox"
        id="cantStopThinking"
        name="cantStopThinking"
        checked={formData.substanceUse.socialMediaAddiction.cantStopThinking}
        onChange={() => handleSocialMediaAddiction('cantStopThinking')}
        className="mr-2"
      />
      <label htmlFor="cantStopThinking">Can't stop thinking about social media</label>
    </div>

    {/* Want More Time */}
    <div className="flex items-center">
      <input
        type="checkbox"
        id="wantMoreTime"
        name="wantMoreTime"
        checked={formData.substanceUse.socialMediaAddiction.wantMoreTime}
        onChange={() => handleSocialMediaAddiction('wantMoreTime')}
        className="mr-2"
      />
      <label htmlFor="wantMoreTime">Want to spend more time on social media</label>
    </div>

    {/* Failed to Reduce */}
    <div className="flex items-center">
      <input
        type="checkbox"
        id="failedToReduce"
        name="failedToReduce"
        checked={formData.substanceUse.socialMediaAddiction.failedToReduce}
        onChange={() => handleSocialMediaAddiction('failedToReduce')}
        className="mr-2"
      />
      <label htmlFor="failedToReduce">Failed to reduce social media use</label>
    </div>

    {/* Neglect Other Activities */}
    <div className="flex items-center">
      <input
        type="checkbox"
        id="neglectActivities"
        name="neglectActivities"
        checked={formData.substanceUse.socialMediaAddiction.neglectActivities}
        onChange={() => handleSocialMediaAddiction('neglectActivities')}
        className="mr-2"
      />
      <label htmlFor="neglectActivities">Neglect other activities due to social media</label>
    </div>
  </div>
</div>
          </div>
          <div className="space-y-4 sm:space-y-0 sm:flex sm:gap-4 mb-6 mt-8">
            <button
              type="submit"
              className="w-full sm:w-2/3 group relative px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <span className="font-semibold">Complete Setup</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            
            <button
              type="button"
              onClick={handleSkip}
              className="w-full sm:w-1/3 group relative px-8 py-4 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <Clock3 className="w-5 h-5 text-gray-500 group-hover:rotate-12 transition-transform" />
              <span className="font-medium">Setup Later</span>
            </button>
          </div>

          {isSaved && (
            <p className="text-green-500 text-center font-medium animate-fade-in">
              {saveMessage}
            </p>
          )}
        </form>

        {/* Updated Skip Dialog */}
        {showSkipDialog && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-900">Skip Profile Setup?</h3>
                <button
                  onClick={() => setShowSkipDialog(false)}
                  className="text-gray-400 hover:text-gray-600 hover:rotate-90 transition-all duration-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="mb-8">
                <p className="text-gray-600 leading-relaxed">
                  If you skip this step, you'll receive basic health tips and recommendations. For the best experience and personalized health advice tailored to your specific needs, we recommend completing your health profile. You can always come back and complete it later.
                </p>
              </div>
              
              <div className="flex flex-col-reverse sm:flex-row-reverse gap-3">
                <button
                  onClick={confirmSkip}
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group"
                >
                  <span className="font-semibold">Skip for Now</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={() => setShowSkipDialog(false)}
                  className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-xl hover:from-gray-200 hover:to-gray-300 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <span className="font-medium">Continue Setup</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default OnboardingPage;